// CallVorgangZuweisenDialog.tsx — „Vorgängen zuordnen" (Telefonie).
//
// Der zweite Menüpunkt des Knopfes „Vorgang"; der erste legt einen neuen an
// (`callVorgangFlow.ts`). Seit 01.10.2026 (PO: „Ein Anruf kann man mehreren
// Vorgängen (und mehreren To-dos innerhalb eines Vorgangs) zuweisen.") ist es
// dieselbe Ausklapp-Mehrfachauswahl wie im Mail-Picker „Bestehendem Vorgang
// zuordnen" (`VorgangMehrfachPicker`, F3): Rangstufen, Bereich, To-dos
// aufklappen und anhaken, „Neues To-do…".
//
//  - **Bestehende Zuordnungen stehen angehakt oben** („Aktuell zugeordnet").
//    Abhaken löst sie, Anhaken fügt hinzu — erst beim „Übernehmen": zuerst
//    entstehen neue To-dos, dann `POST …/zuordnungen` für das Neue, dann
//    `DELETE` je abgehakter Zuordnung.
//  - **Ohne Kontakt geht es trotzdem.** Ein bestehender Vorgang hat seinen
//    Kunden schon; der Server hängt ihn dann als Hauptkontakt an DIESEN Anruf
//    (T-53), ohne die Nummer beim Kunden zu speichern.
//  - **Kontakt-Rückfrage (F2):** Kennt ein Zielvorgang einen Kontakt des
//    Anrufs nicht, fragt danach ein kvp-rd-Popup, ob er aufgenommen wird.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { VorgangMehrfachPicker, type VorbelegtesZiel } from "../vorgang/picker/VorgangMehrfachPicker";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { loese, serverMeldung, weiseZu } from "./callVorgangZuweisen";
import { zuordnungLabel } from "./callZuordnungen";
import { displayNameOf } from "./telephonyUtils";
import "../redesign/contactRedesign.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const CloseIcon = (
    <svg
        width="17"
        height="17"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
    >
        <path d="M5 5l10 10M15 5L5 15" />
    </svg>
);

export interface CallVorgangZuweisenDialogProps {
    call: CallLogDto;
    onClose: () => void;
    /** Der zugeordnete (und ggf. erledigte) Anruf — für Liste und Detail. */
    onAssigned: (next: CallLogDto) => void;
}

/** Bestehende Zuordnungen als Vorbelegung — nur echte (mit Id), lösbar. */
export function vorbelegungAus(call: CallLogDto): VorbelegtesZiel[] {
    return (call.zuordnungen ?? []).map((z) => ({
        vorgangId: z.vorgangId,
        todoId: z.todoId,
        titel: zuordnungLabel(z),
        zuordnungId: z.id,
    }));
}

/** Beschriftung des Hauptknopfs nach dem, was sich ändert. */
export function knopfText({ hinzu, weg }: { hinzu: number; weg: number }): string {
    if (hinzu > 0 && weg > 0) return "Übernehmen";
    if (weg > 0) return weg > 1 ? `${weg} Zuordnungen lösen` : "Zuordnung lösen";
    if (hinzu > 1) return `${hinzu} Zielen zuordnen`;
    return "Zuordnen";
}

const CallVorgangZuweisenDialog: React.FC<CallVorgangZuweisenDialogProps> = ({
    call,
    onClose,
    onAssigned,
}) => {
    // Die Zuordnungen MIT Id: Fehlen sie in der Zeile (ältere Liste), einmal
    // frisch holen — sonst ließe sich nichts gezielt lösen.
    const [aktuell, setAktuell] = useState<CallLogDto | null>(Array.isArray(call.zuordnungen) ? call : null);
    const [ladeFehler, setLadeFehler] = useState("");
    useEffect(() => {
        if (aktuell) return;
        let ab = false;
        telephonyService
            .listCalls({ callId: call.callId, limit: 1, mitZuordnungen: true })
            .then((r) => {
                if (ab) return;
                const zeile = r.calls.find((c) => c.id === call.id);
                setAktuell(zeile && Array.isArray(zeile.zuordnungen) ? zeile : { ...call, zuordnungen: [] });
            })
            .catch(() => {
                if (ab) return;
                setLadeFehler("Die bisherigen Zuordnungen konnten nicht geladen werden.");
                setAktuell({ ...call, zuordnungen: [] });
            });
        return () => {
            ab = true;
        };
    }, [aktuell, call]);

    const [saving, setSaving] = useState(false);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !saving) onClose();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [onClose, saving]);

    const vorbelegt = useMemo(() => (aktuell ? vorbelegungAus(aktuell) : []), [aktuell]);

    const uebernehmen = useCallback(
        async ({ hinzu, weg }: { hinzu: { vorgangId: number; todoId: number | null }[]; weg: VorbelegtesZiel[] }) => {
            if (!aktuell) return;
            setSaving(true);
            try {
                let next = aktuell;
                if (hinzu.length > 0) {
                    next = await weiseZu(
                        aktuell,
                        hinzu.map((h) => ({ vorgangId: h.vorgangId, todoId: h.todoId })),
                    );
                }
                for (const w of weg) {
                    if (w.zuordnungId == null) continue;
                    // Eine Vorgangs-Zuordnung, in deren Vorgang jetzt ein To-do
                    // dazukam, hat der Server schon zum To-do umgewandelt — sie
                    // zu löschen träfe die neue Zuordnung.
                    if (w.todoId == null && hinzu.some((h) => h.vorgangId === w.vorgangId && h.todoId != null)) {
                        continue;
                    }
                    next = await loese(aktuell, w.zuordnungId);
                }
                onAssigned(next);
                onClose();
            } finally {
                setSaving(false);
            }
        },
        [aktuell, onAssigned, onClose],
    );

    const fehlerText = useCallback(
        (error: unknown, art: "anlegen" | "zuordnen") =>
            serverMeldung(error) ??
            (art === "anlegen"
                ? "Das To-do konnte nicht angelegt werden."
                : "Der Anruf konnte nicht zugeordnet werden."),
        [],
    );

    const verborgen = aktuell?.weitereZuordnungenVerborgen ?? 0;

    return createPortal(
        <div className="kvp-rd kvp-rd-scrim" onClick={saving ? undefined : onClose}>
            <div
                className="dm"
                role="dialog"
                aria-modal="true"
                aria-label="Anruf Vorgängen zuordnen"
                style={{ width: "min(600px, 100%)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Vorgängen zuordnen</div>
                            <h2>Anruf von {displayNameOf(call)}</h2>
                        </div>
                        <button
                            className="dm-x"
                            type="button"
                            onClick={onClose}
                            aria-label="Schließen"
                            disabled={saving}
                        >
                            {CloseIcon}
                        </button>
                    </div>
                </div>

                <div className="dm-body tp-vgmehrfach" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div className="tp-vgmehrfach-hinweis">
                        Ein Anruf kann mehreren Vorgängen und To-dos zugeordnet sein.
                        {call.contactId == null &&
                            " Der Anruf hat noch keinen Kontakt; er bekommt den Kunden des ersten gewählten Vorgangs."}
                        {verborgen > 0 &&
                            ` Dazu ${verborgen === 1 ? "kommt eine weitere Zuordnung" : `kommen ${verborgen} weitere Zuordnungen`} an Vorgängen, die du nicht sehen darfst.`}
                    </div>
                    {ladeFehler && <div className="rcp-err">{ladeFehler}</div>}

                    <div className="tp-vgmehrfach-picker">
                        {aktuell ? (
                            <VorgangMehrfachPicker
                                contactId={call.contactId ?? null}
                                vorbelegt={vorbelegt}
                                onUebernehmen={uebernehmen}
                                onAbbrechen={onClose}
                                fehlerText={fehlerText}
                                knopfText={knopfText}
                            />
                        ) : (
                            <div className="tp-muted">Zuordnungen werden geladen …</div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default CallVorgangZuweisenDialog;
